import { useState, useEffect, useRef, useMemo } from "react";
import { MAP_LAYER_COLORS, MAP_LAYER_LABELS } from "../../constants/mapLayers";
import { pinField, asNumber } from "../../utils/pinFields";
import { streetViewThumbUrl, hasStreetViewKey } from "../../services/streetView";
import { addressKey } from "../../utils/addressKey";

// ═══════════════════════════════════════════════════════════════════════════
// MAP RESULT CARDS
// ═══════════════════════════════════════════════════════════════════════════
// The scrolling panel beside the map. Same pin set the markers and the list
// use, so all three always agree.
//
// Card layout follows the CoStar pattern: image left, three lines of text
// right — address, then locality and building name, then the stats line. The
// address uses PP red rather than CoStar's blue so the panel stays on
// palette.
//
// Paged rather than truncated. An earlier version capped at 300 cards, which
// meant records you could not reach; pages also bound image cost, since a
// page of 20 is at most 20 billed Street View requests.
//
// Thumbnails are off by default and doubly guarded: you opt in with the
// Photos toggle, and even then an IntersectionObserver only requests an image
// once its card is actually on screen.

const PHOTOS_KEY = "pp-map-card-photos";
const PAGE_SIZE_KEY = "pp-map-card-page-size";
const PAGE_SIZES = [20, 50, 100];

function loadPref(key, fallback, parse) {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : parse(v);
  } catch { return fallback; }
}

const money = v => { const n = asNumber(v); return (n == null || n <= 0) ? null : (n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : `$${n.toLocaleString()}`); };
const psf   = v => { const n = asNumber(v); return (n == null || n <= 0) ? null : `$${n.toFixed(2)}/SF`; };
const sfTxt = v => { const n = asNumber(v); return (n == null || n <= 0) ? null : `${n.toLocaleString()} SF`; };

// The stats line — a few facts, chosen by what the record actually carries.
function cardFacts(p) {
  const f = k => pinField(p, k);
  const built = f("year_built") ? `Built ${f("year_built")}` : null;
  if (p.isPipeline) {
    return [sfTxt(f("sf")), f("product_type") ? String(f("product_type")) : null, built, money(f("askingPrice"))];
  }
  if (p.isComp && p.compType === "sale") {
    return [sfTxt(f("sf")), money(f("sale_price")), psf(f("price_psf")), built];
  }
  if (p.isComp) {
    return [sfTxt(f("suite_sf") ?? f("sf")), psf(f("lease_rate")), f("lease_type") ? String(f("lease_type")) : null, built];
  }
  return [sfTxt(f("sf")), built, f("classification") ? `Class ${f("classification")}` : null];
}

function Thumb({ pin, S }) {
  const ref = useRef(null);
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || src || failed) return;
    if (!hasStreetViewKey() || !pin.lat || !pin.lng) { setFailed(true); return; }

    // Resolving a thumbnail is async now - the panorama has to be chosen
    // before the image can be requested by id, which is what keeps other
    // people's interior tours off these cards. `alive` guards the setState:
    // the panel is a scrolling list and a card can unmount mid-lookup.
    let alive = true;
    const load = () => {
      streetViewThumbUrl(pin.lat, pin.lng).then((url) => {
        if (!alive) return;
        if (url) setSrc(url); else setFailed(true);
      }).catch(() => { if (alive) setFailed(true); });
    };

    if (typeof IntersectionObserver === "undefined") { load(); return () => { alive = false; }; }
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) { load(); io.disconnect(); }
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => { alive = false; io.disconnect(); };
  }, [pin.lat, pin.lng, src, failed]);

  return (
    <div ref={ref} style={{ width: 190, minWidth: 190, alignSelf: "stretch", minHeight: 132, background: S.card2 || S.card, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" onError={() => setFailed(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      ) : (
        <span style={{ fontSize: 9, color: S.muted, fontWeight: 600, textAlign: "center", padding: 6 }}>
          {failed ? "No street imagery" : ""}
        </span>
      )}
    </div>
  );
}

// The layer colour is chosen so the PIN reads against satellite tiles — sale
// comps are near-black for exactly that reason. On a dark card the same value
// is invisible, so the chip keeps the true colour on its dot (the tie back to
// the pin) and lightens it for the label until it can actually be read.
function readableOn(hex, floor = 0.16) {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return hex;
  let [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
  const lum = () => {
    const f = (x) => { x /= 255; return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4); };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
  };
  let guard = 0;
  while (lum() < floor && guard++ < 24) {
    r = Math.round(r + (244 - r) * 0.18);
    g = Math.round(g + (244 - g) * 0.18);
    b = Math.round(b + (244 - b) * 0.18);
  }
  return `rgb(${r},${g},${b})`;
}

export default function MapResultCards({ pins, S, onOpenPin, onClose }) {
  const [photos, setPhotos] = useState(() => loadPref(PHOTOS_KEY, false, v => v === "true"));
  const [pageSize, setPageSize] = useState(() => loadPref(PAGE_SIZE_KEY, 20, v => {
    const n = parseInt(v, 10);
    return PAGE_SIZES.includes(n) ? n : 20;
  }));
  const [page, setPage] = useState(0);

  useEffect(() => { try { localStorage.setItem(PHOTOS_KEY, String(photos)); } catch {} }, [photos]);
  useEffect(() => { try { localStorage.setItem(PAGE_SIZE_KEY, String(pageSize)); } catch {} }, [pageSize]);

  // One card per thing, not per row. A deal spanning five addresses was five
  // identical cards; four lease comps in one building were four. Deals group
  // by deal id (the deal is the unit), everything else by address within its
  // type — the same rule the map pins use.
  const grouped = useMemo(() => {
    const byKey = new Map();
    (pins || []).forEach(p => {
      const key = (p.isPipeline && p.dealId)
        ? `deal|${p.dealId}`
        : `${p.layerKey}|${addressKey(p.address) || p.id}`;
      const g = byKey.get(key);
      if (g) g.push(p); else byKey.set(key, [p]);
    });
    return [...byKey.values()];
  }, [pins]);

  const recordCount = (pins || []).length;
  const total = grouped.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount - 1);

  // Reset to the first page whenever the underlying set changes shape.
  useEffect(() => { setPage(0); }, [total, pageSize]);

  const shown = useMemo(
    () => grouped.slice(safePage * pageSize, safePage * pageSize + pageSize),
    [grouped, safePage, pageSize]
  );

  const smallBtn = (active) => ({
    background: active ? S.red : "transparent",
    border: `1px solid ${active ? S.red : S.border}`,
    borderRadius: 6, color: active ? "#ffffff" : S.muted,
    cursor: "pointer", fontSize: 10, fontWeight: 800,
    padding: "3px 8px", fontFamily: S.font, whiteSpace: "nowrap",
  });

  return (
    <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 520, zIndex: 520, background: S.sidebar, borderLeft: `1px solid ${S.border}`, display: "flex", flexDirection: "column", fontFamily: S.font, color: S.text }}>

      <style>{`
        .mrc-row:hover { background: ${S.card} !important; }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "9px 12px", borderBottom: `1px solid ${S.border}`, flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800 }}>{total.toLocaleString()} on the map</div>
          {recordCount !== total && (
            <div style={{ fontSize: 9.5, color: S.muted, marginTop: 1 }}>
              from {recordCount.toLocaleString()} records
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <button onClick={() => setPhotos(v => !v)}
            title={photos
              ? "Turn photos off — each thumbnail is a billed Street View request"
              : "Load Street View thumbnails (billed per image, only for cards on screen)"}
            style={smallBtn(photos)}>
            Photos {photos ? "on" : "off"}
          </button>
          <button onClick={onClose} title="Hide cards" style={{ ...smallBtn(false), fontSize: 12 }}>›</button>
        </div>
      </div>

      {/* Cards */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {shown.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", fontSize: 11.5, color: S.muted }}>
            No pins match the current filters.
          </div>
        )}
        {shown.map((members, i) => {
          const p = members[0];
          const stack = members.length;
          // A deal's stack is extra addresses; anything else is extra records.
          const stackLabel = stack < 2 ? null
            : p.isPipeline ? `${stack} addresses` : `${stack} records`;
          const color = MAP_LAYER_COLORS[p.layerKey] || "#6a6a6a";
          const facts = cardFacts(p).filter(Boolean);
          const locality = [pinField(p, "city"), pinField(p, "state")].filter(Boolean).join(", ")
            || pinField(p, "market") || null;
          const building = pinField(p, "property_name") || null;
          return (
            <div key={`${p.id}-${i}`}
              onClick={() => onOpenPin && onOpenPin(p)}
              className="mrc-row"
              style={{ display: "flex", gap: 12, background: "transparent", borderBottom: `1px solid ${S.border}`, padding: 0, cursor: onOpenPin ? "pointer" : "default" }}>
              {photos && <Thumb pin={p} S={S} />}
              <div style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 4, padding: photos ? "12px 12px 12px 0" : "12px" }}>
                {/* Address — the strong line */}
                <div style={{ fontSize: 14, fontWeight: 800, color: S.red, lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p._dealName || p.address || "Untitled"}
                </div>
                {/* Locality · building name */}
                {(locality || building) && (
                  <div style={{ fontSize: 12.5, color: S.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {[locality, building].filter(Boolean).join(" · ")}
                  </div>
                )}
                {/* Stats */}
                {facts.length > 0 && (
                  <div style={{ fontSize: 12.5, color: S.text, opacity: 0.9, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {facts.join(" · ")}
                  </div>
                )}
                <div style={{ marginTop: "auto", paddingTop: 6 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "1px 7px", borderRadius: 10, background: `${color}1f`, border: `1px solid ${color}55` }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
                    <span style={{ fontSize: 9, fontWeight: 800, color: readableOn(color) }}>
                      {MAP_LAYER_LABELS[p.layerKey] || p.bucket || p.layerKey}
                    </span>
                  </span>
                  {stackLabel && (
                    <span style={{ marginLeft: 6, display: "inline-flex", alignItems: "center", padding: "1px 7px", borderRadius: 10, background: S.card2 || S.border, fontSize: 9, fontWeight: 800, color: S.muted }}>
                      {stackLabel}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pager */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 12px", borderTop: `1px solid ${S.border}`, flexShrink: 0, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={safePage === 0}
            style={{ ...smallBtn(false), opacity: safePage === 0 ? 0.4 : 1, cursor: safePage === 0 ? "not-allowed" : "pointer" }}>‹</button>
          <span style={{ fontSize: 10.5, color: S.muted, fontVariantNumeric: "tabular-nums" }}>
            {total === 0 ? "0" : `${(safePage * pageSize + 1).toLocaleString()}–${Math.min((safePage + 1) * pageSize, total).toLocaleString()}`} of {total.toLocaleString()}
          </span>
          <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={safePage >= pageCount - 1}
            style={{ ...smallBtn(false), opacity: safePage >= pageCount - 1 ? 0.4 : 1, cursor: safePage >= pageCount - 1 ? "not-allowed" : "pointer" }}>›</button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: 9.5, color: S.muted, fontWeight: 700 }}>Show</span>
          {PAGE_SIZES.map(n => (
            <button key={n} onClick={() => setPageSize(n)} style={smallBtn(pageSize === n)}>{n}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
